import React, { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight, Clock, ExternalLink, Facebook } from "lucide-react";
import { clinicInfo } from "../mock";

export default function Contact() {
  const [copied, setCopied] = useState(false);

  const copyPhone = () => {
    navigator.clipboard.writeText(clinicInfo.phone).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section id="contact" className="relative px-4 md:px-8 pb-20 md:pb-28">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-10">
          <div>
            <span className="eyebrow">Get in touch</span>
            <h2 className="section-title">Book an appointment</h2>
          </div>
          <p className="text-[#4a4a63] leading-relaxed lg:max-w-lg lg:ml-auto">
            Bookings are made online via HotDoc or by calling reception. <span className="font-semibold text-[#1a2464]">100% bulk billed</span> with a valid Medicare card — new and existing patients welcome.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-8">
          {/* Left navy card */}
          <div className="relative rounded-[32px] bg-[#1a2464] text-white overflow-hidden p-8 md:p-10 flex flex-col justify-between">
            <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />
            <div className="relative space-y-8">
              <ContactItem icon={<Phone className="w-5 h-5" />} label="Call Reception">
                <div className="text-white/70 text-sm">Book by phone — friendly reception team.</div>
                <a
                  href={`tel:${clinicInfo.phoneRaw}`}
                  className="block text-2xl font-bold text-white hover:text-[#3dc9b3] mt-1"
                >
                  {clinicInfo.phone}
                </a>
                <button
                  onClick={copyPhone}
                  className="mt-2 text-[#3dc9b3] text-xs font-semibold hover:underline"
                >
                  {copied ? "Copied ✓" : "Copy number"}
                </button>
              </ContactItem>

              <ContactItem icon={<MapPin className="w-5 h-5" />} label="Visit Us">
                <div className="font-bold text-white">Beaudesert General Practice</div>
                <div className="text-white/70 text-sm leading-relaxed">
                  35A William Street<br />Beaudesert QLD 4285<br />
                  (Short walk from Beaudesert town centre)
                </div>
              </ContactItem>

              <ContactItem icon={<Clock className="w-5 h-5" />} label="Opening Hours">
                <div className="text-white/70 text-sm leading-relaxed">
                  {clinicInfo.hours.weekdays}<br />
                  {clinicInfo.hours.saturday}<br />
                  {clinicInfo.hours.sunday}
                </div>
              </ContactItem>

              <ContactItem icon={<Mail className="w-5 h-5" />} label="Email">
                <a
                  href={`mailto:${clinicInfo.email}`}
                  className="text-white hover:text-[#3dc9b3] break-all"
                >
                  {clinicInfo.email}
                </a>
              </ContactItem>
            </div>

            <div className="relative mt-8 flex flex-wrap gap-3">
              <a
                href={clinicInfo.hotdocUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold pl-5 pr-2 py-2 rounded-full hover:bg-[#34b39f]"
              >
                <span>Book on HotDoc</span>
                <span className="w-8 h-8 rounded-full bg-white/20 grid place-items-center group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight className="w-4 h-4 text-white" />
                </span>
              </a>
              <a
                href={clinicInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/25 text-white text-sm px-5 py-2 rounded-full hover:bg-white/10"
              >
                <Facebook className="w-4 h-4" /> Facebook
              </a>
            </div>
          </div>

          {/* Map */}
          <div className="relative rounded-[32px] overflow-hidden shadow-lg bg-white min-h-[420px]">
            <iframe
              title="Beaudesert General Practice location"
              src={clinicInfo.mapEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
            />
            <a
              href={clinicInfo.mapDirections}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-5 left-5 md:bottom-6 md:left-6 inline-flex items-center gap-2 bg-white text-[#1a2464] font-semibold text-sm px-5 py-3 rounded-full shadow-lg hover:bg-[#f6f5fd]"
            >
              <MapPin className="w-4 h-4 text-[#3dc9b3]" />
              Get Directions
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-11 h-11 rounded-full bg-[#3dc9b3] grid place-items-center text-white">{icon}</div>
      <div className="space-y-1 min-w-0">
        <div className="font-bold text-white">{label}</div>
        {children}
      </div>
    </div>
  );
}
